import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  FaTimes, FaCheck, FaUser, FaSearch, FaSpinner, FaExclamationTriangle,
} from 'react-icons/fa';
import './ModalBaixaPagador.css';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

/**
 * Modal de baixa com seleção de pagador.
 * Abre somente quando o toggle "Exibir pagador" do tenant está ligado.
 * Serve tanto para baixa unitária quanto em lote (recebe a lista de contas).
 */
export default function ModalBaixaPagador({
  isOpen,
  contas = [],
  pagadores = [],
  submitting = false,
  onClose,
  onConfirm,
}) {
  const [pagadorId,   setPagadorId]   = useState(null);
  const [busca,       setBusca]       = useState('');
  const [listaAberta, setListaAberta] = useState(false);
  const buscaRef = useRef(null);
  const wrapRef  = useRef(null);

  const total = useMemo(
    () => contas.reduce((a, c) => a + parseFloat(c.valor || 0), 0),
    [contas]
  );

  const pagadorSel = useMemo(
    () => pagadores.find((p) => p.id === pagadorId) || null,
    [pagadores, pagadorId]
  );

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return pagadores;
    return pagadores.filter(
      (p) =>
        p.nome.toLowerCase().includes(q) ||
        String(p.documento || '').toLowerCase().includes(q)
    );
  }, [pagadores, busca]);

  const semPagadores  = pagadores.length === 0;
  const podeConfirmar = !!pagadorId && !submitting && !semPagadores;

  // Reset + foco ao abrir
  useEffect(() => {
    if (isOpen) {
      setPagadorId(null);
      setBusca('');
      setListaAberta(false);
      const t = setTimeout(() => buscaRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Fecha a lista ao clicar fora do combo
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setListaAberta(false);
    };
    if (listaAberta) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [listaAberta]);

  const confirmar = useCallback(() => {
    if (!podeConfirmar) return;
    onConfirm?.(pagadorId);
  }, [podeConfirmar, onConfirm, pagadorId]);

  // Atalhos de teclado
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'Enter' && podeConfirmar) confirmar();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, podeConfirmar, confirmar]);

  if (!isOpen) return null;

  const selecionar = (p) => {
    setPagadorId(p.id);
    setBusca('');
    setListaAberta(false);
  };

  return (
    <div className="mbp-overlay" onMouseDown={onClose}>
      <div
        className="mbp-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar baixa de conta"
      >
        {/* Header */}
        <div className="mbp-header">
          <div className="mbp-header-title">
            <span className="mbp-header-icon"><FaCheck /></span>
            <div>
              <h2>Confirmar baixa</h2>
              <p>{contas.length} conta{contas.length !== 1 ? 's' : ''} · selecione o pagador</p>
            </div>
          </div>
          <button className="mbp-close" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        </div>

        {/* Resumo */}
        <div className="mbp-summary">
          <div className="mbp-summary-item">
            <span className="mbp-summary-label">Contas</span>
            <strong className="mbp-summary-value">{contas.length}</strong>
          </div>
          <div className="mbp-summary-divider" />
          <div className="mbp-summary-item">
            <span className="mbp-summary-label">Total a baixar</span>
            <strong className="mbp-summary-value mbp-total">{fmtCurrency(total)}</strong>
          </div>
        </div>

        {/* Contas */}
        {contas.length > 1 ? (
          <div className="mbp-contas-list">
            {contas.slice(0, 6).map((c) => (
              <div className="mbp-conta-row" key={c.id}>
                <span className="mbp-conta-desc" title={c.descricao}>{c.descricao}</span>
                <span className="mbp-conta-val">{fmtCurrency(c.valor)}</span>
              </div>
            ))}
            {contas.length > 6 && (
              <div className="mbp-conta-more">+{contas.length - 6} outra(s)…</div>
            )}
          </div>
        ) : contas.length === 1 ? (
          <div className="mbp-single-desc" title={contas[0]?.descricao}>
            {contas[0]?.descricao}
          </div>
        ) : null}

        {/* Seletor de pagador */}
        <div className="mbp-field">
          <label className="mbp-field-label"><FaUser /> Pagador</label>

          {semPagadores ? (
            <div className="mbp-empty-pagadores">
              <FaExclamationTriangle />
              <span>
                Nenhum pagador cadastrado. Solicite ao Administrador Geral
                o cadastro de pagadores antes de dar baixa.
              </span>
            </div>
          ) : pagadorSel ? (
            <div className="mbp-selected">
              <div className="mbp-selected-info">
                <span className="mbp-selected-avatar">
                  {pagadorSel.nome.charAt(0).toUpperCase()}
                </span>
                <div className="mbp-selected-meta">
                  <strong>{pagadorSel.nome}</strong>
                  {pagadorSel.documento && (
                    <span className="mbp-selected-doc">{pagadorSel.documento}</span>
                  )}
                </div>
              </div>
              <button
                className="mbp-trocar"
                onClick={() => {
                  setPagadorId(null);
                  setListaAberta(true);
                  setTimeout(() => buscaRef.current?.focus(), 50);
                }}
              >
                Trocar
              </button>
            </div>
          ) : (
            <div className="mbp-combo" ref={wrapRef}>
              <div className="mbp-combo-input">
                <FaSearch className="mbp-combo-search-icon" />
                <input
                  ref={buscaRef}
                  type="text"
                  placeholder="Buscar pagador por nome ou documento…"
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setListaAberta(true); }}
                  onFocus={() => setListaAberta(true)}
                />
              </div>
              {listaAberta && (
                <div className="mbp-combo-list">
                  {filtrados.length === 0 ? (
                    <div className="mbp-combo-empty">Nenhum pagador encontrado</div>
                  ) : (
                    filtrados.map((p) => (
                      <button
                        key={p.id}
                        className="mbp-combo-option"
                        onClick={() => selecionar(p)}
                      >
                        <span className="mbp-combo-avatar">
                          {p.nome.charAt(0).toUpperCase()}
                        </span>
                        <span className="mbp-combo-name">{p.nome}</span>
                        {p.documento && <span className="mbp-combo-doc">{p.documento}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mbp-footer">
          <button className="mbp-btn-cancel" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button className="mbp-btn-confirm" onClick={confirmar} disabled={!podeConfirmar}>
            {submitting
              ? <><FaSpinner className="mbp-spin" /> Processando…</>
              : <><FaCheck /> Confirmar baixa</>}
          </button>
        </div>
      </div>
    </div>
  );
}